/**
 * Cómo se ordenan las líneas de una prenómina. Un solo motor para los dos
 * lugares donde el usuario elige un orden:
 *
 *   · TablaPrenomina — clic en los encabezados Cód. / clave / Empleado / Neto.
 *   · ModalVerRecibos — el selector "Ordenar por" del visor de recibos, que
 *     además de esos cuatro ofrece departamento, registro patronal, proyecto
 *     y RFC (OPCIONES_ORDEN).
 *
 * Están juntos a propósito: si "por código" ordenara distinto en la tabla que
 * en el paquete impreso, el usuario lo leería como un bug. Por lo mismo el
 * módulo vive en la carpeta de PreNomina y no dentro de ninguno de los dos —
 * es compartido entre hermanos, no una pieza interna de uno.
 *
 * En el visor de recibos elegir el orden es literalmente elegir en qué orden se
 * mandan los ids: el backend arma el PDF concatenado respetando el orden del
 * arreglo `lineaIds` que recibe (Controllers/Prenomina/cierre.controllers.js —
 * _datosRecibosLote no garantiza orden, el llamador recorre sus ids).
 *
 * Las jerarquías de departamento, registro patronal y proyecto son a propósito
 * las mismas que usa la Lista de Raya (TablaPrenomina.js), para que el paquete
 * impreso y ese Excel salgan agrupados igual.
 */

// ─── Neto de una línea ───────────────────────────────────────────────────────
//
// No se lee `linea.totalNeto`: ese total se persiste al calcular y queda
// desfasado si después se editaron importes a mano sin recalcular. La tabla de
// prenómina no lo usa por ese mismo motivo, y si el orden por Neto no coincidiera
// con la columna Neto de la pantalla se leería como bug.

const importeEfectivo = (c) => {
  const esManual = c.importeTotalManual !== null && c.importeTotalManual !== undefined
  return esManual ? c.importeTotalManual : (c.importeTotal || 0)
}

/** Set de ids (string) de los conceptos marcados noSuma — informativos, nunca suman. */
export const idsNoSuma = (conceptosNomina) => new Set(
  (conceptosNomina || [])
    .filter(c => c.noSuma)
    .map(c => c._id?.toString())
)

/**
 * Set de ids (string) de las percepciones en especie — se entregan en bienes, no en dinero.
 * A diferencia de los noSuma NO son informativas: se timbran y gravan como cualquier
 * percepción. Lo único que no hacen es sumar al neto a pagar, así que van en un set aparte
 * para que nadie las pinte como "Info".
 */
export const idsEspecie = (conceptosNomina) => new Set(
  (conceptosNomina || [])
    .filter(c => c.tipo === 1 && c.especie && !c.noSuma)
    .map(c => c._id?.toString())
)

/**
 * Percepciones menos deducciones de UNA línea. Quedan fuera los conceptos noSuma
 * (informativos) y las percepciones en especie (no son entrega en dinero).
 * Se toma el catálogo como fuente de verdad y se cae al flag de la línea, porque un
 * concepto agregado a mano puede no estar en el catálogo cargado.
 */
export const netoDeLinea = (linea, noSumaIds, especieIds) => {
  const fuera = noSumaIds || new Set()
  const esp   = especieIds || new Set()
  const suma = (tipo) => (linea.conceptos || [])
    .filter(c => c.tipo === tipo && !fuera.has(c.conceptoNominaId?.toString()) && !c.noSuma)
    .filter(c => tipo !== 1 || !(esp.has(c.conceptoNominaId?.toString()) || c.especie))
    .reduce((s, c) => s + importeEfectivo(c), 0)
  return suma(1) - suma(2)
}

// ─── Comparadores ────────────────────────────────────────────────────────────
//
// REGLA: en los campos de clasificación (código, departamento, registro
// patronal, proyecto, RFC) los vacíos SIEMPRE quedan al final, también en
// descendente. El "tiene valor / no tiene valor" se resuelve antes de aplicar
// `dir`; sólo la comparación entre dos valores presentes se invierte. Si se
// negara el comparador completo, al invertir el orden los empleados sin
// departamento o sin RFC brincarían al principio del paquete impreso.
//
// Los apellidos son la excepción y van con cmpNombreParte — ver ahí el motivo.

const cmpTexto = (a, b, dir) => {
  const va = a === null || a === undefined ? '' : String(a).trim()
  const vb = b === null || b === undefined ? '' : String(b).trim()
  if (!va && !vb) return 0
  if (!va) return 1
  if (!vb) return -1
  // numeric: true para que "0012" y "12" empaten y "EMP9" vaya antes que "EMP10".
  // Comparar con Number() en su lugar convierte los códigos alfanuméricos en NaN
  // y la comparación se pierde en silencio.
  return dir * va.localeCompare(vb, 'es', { numeric: true, sensitivity: 'base' })
}

const cmpNumero = (a, b, dir) => {
  const num = (v) => {
    if (v === null || v === undefined || v === '') return null
    const n = Number(v)
    return Number.isNaN(n) ? null : n
  }
  const va = num(a)
  const vb = num(b)
  if (va === null && vb === null) return 0
  if (va === null) return 1
  if (vb === null) return -1
  return dir * (va - vb)
}

// Los apellidos NO llevan la regla de "vacíos al final": que a alguien le falte
// el materno no es un registro sin clasificar, es un apellido más corto, y
// mandarlo al final del grupo de su paterno se lee como un acomodo raro
// (además de que hoy el orden por defecto los intercala). Aquí el vacío ordena
// antes, como la cadena vacía que es.
const cmpNombreParte = (a, b, dir) => {
  const va = a === null || a === undefined ? '' : String(a).trim()
  const vb = b === null || b === undefined ? '' : String(b).trim()
  if (va === vb) return 0
  return dir * va.localeCompare(vb, 'es', { numeric: true, sensitivity: 'base' })
}

// Desempate final de todos los criterios: sin él dos corridas del mismo orden
// podrían devolver documentos distintos.
const porNombre = (a, b, dir) =>
  cmpNombreParte(a.apellidoPaterno, b.apellidoPaterno, dir) ||
  cmpNombreParte(a.apellidoMaterno, b.apellidoMaterno, dir) ||
  cmpNombreParte(a.nombre, b.nombre, dir)

const porCodigo = (a, b, dir) => cmpTexto(a.codigo, b.codigo, dir)

// El consecutivo interno del empleado (EMP12) — lo que en la tabla se ve en gris
// debajo del código. Se llama 'serieNumeroEmpleado' y no 'serieNumero' porque en
// la línea ese otro nombre ya es el del CONCEPTO, no el del empleado.
const porClave = (a, b, dir) => cmpTexto(a.serieNumeroEmpleado, b.serieNumeroEmpleado, dir)

const porDepartamento = (a, b, dir) =>
  cmpNumero(a.departamentoNumero, b.departamentoNumero, dir) ||
  cmpTexto(a.departamentoNombre, b.departamentoNombre, dir)

// ─── Criterios ───────────────────────────────────────────────────────────────

export const ORDEN_POR_DEFECTO = 'nombre'

/**
 * Todos los criterios disponibles: `criterio -> comparador(a, b, dir, noSumaIds)`,
 * donde `dir` es 1 (ascendente) o -1. Cada uno cierra con `porNombre` para que el
 * orden sea determinista.
 *
 * No todos salen en las dos pantallas: la tabla usa cuatro por sus encabezados y
 * el visor de recibos los que estén en OPCIONES_ORDEN.
 */
export const COMPARADORES = {
  nombre: (a, b, dir) => porNombre(a, b, dir),

  codigo: (a, b, dir) => porCodigo(a, b, dir) || porNombre(a, b, dir),

  clave: (a, b, dir) => porClave(a, b, dir) || porNombre(a, b, dir),

  departamento: (a, b, dir) =>
    porDepartamento(a, b, dir) || porCodigo(a, b, dir) || porNombre(a, b, dir),

  registroPatronal: (a, b, dir) =>
    cmpNumero(a.registroPatronalNumero, b.registroPatronalNumero, dir) ||
    cmpTexto(a.registroPatronalIMSS, b.registroPatronalIMSS, dir) ||
    porDepartamento(a, b, dir) || porCodigo(a, b, dir) || porNombre(a, b, dir),

  // Proyecto no tiene clave ni número, sólo nombre: el orden es alfabético y los
  // que no tienen ninguno caen al final por la regla de vacíos.
  proyecto: (a, b, dir) =>
    cmpTexto(a.proyectoNombre, b.proyectoNombre, dir) ||
    porDepartamento(a, b, dir) || porCodigo(a, b, dir) || porNombre(a, b, dir),

  neto: (a, b, dir, noSumaIds, especieIds) =>
    cmpNumero(netoDeLinea(a, noSumaIds, especieIds), netoDeLinea(b, noSumaIds, especieIds), dir) ||
    porNombre(a, b, dir),

  rfc: (a, b, dir) => cmpTexto(a.rfc, b.rfc, dir) || porNombre(a, b, dir),
}

/**
 * Copia ordenada de las líneas. Nunca muta el arreglo que recibe: los dos
 * consumidores le pasan el mismo arreglo que tienen en props o en state.
 * Un criterio desconocido cae al orden por defecto.
 */
export const ordenarLineas = (lineas, criterio, descendente, noSumaIds, especieIds) => {
  const comparador = COMPARADORES[criterio] || COMPARADORES[ORDEN_POR_DEFECTO]
  const dir = descendente ? -1 : 1
  return [...(lineas || [])].sort((a, b) => comparador(a, b, dir, noSumaIds, especieIds))
}

// ─── Catálogo del selector del visor de recibos ──────────────────────────────
//
// El subconjunto de COMPARADORES que se ofrece al imprimir, con su etiqueta y el
// sufijo que va en el nombre del archivo al descargar. 'clave' queda fuera a
// propósito: es un criterio de la tabla, y esta lista es la que el usuario pidió
// para la impresión.

export const OPCIONES_ORDEN = [
  { valor: 'nombre',           etiqueta: 'Nombre',             sufijo: '' },
  { valor: 'codigo',           etiqueta: 'Código de empleado',  sufijo: '_porCodigo' },
  { valor: 'departamento',     etiqueta: 'Departamento',        sufijo: '_porDepartamento' },
  { valor: 'registroPatronal', etiqueta: 'Registro patronal',   sufijo: '_porRegistroPatronal' },
  { valor: 'proyecto',         etiqueta: 'Proyecto',            sufijo: '_porProyecto' },
  { valor: 'neto',             etiqueta: 'Neto',                sufijo: '_porNeto' },
  { valor: 'rfc',              etiqueta: 'RFC',                 sufijo: '_porRfc' },
]

/** Sufijo del nombre de archivo para un criterio (vacío para el orden por defecto). */
export const sufijoOrden = (criterio, descendente) => {
  const opcion = OPCIONES_ORDEN.find(o => o.valor === criterio) ||
    OPCIONES_ORDEN.find(o => o.valor === ORDEN_POR_DEFECTO)
  const base = opcion.sufijo
  if (!descendente) return base
  return base ? `${base}Desc` : '_desc'
}
